import { useEffect, useState } from "react";
import { useAppStateProvider } from "../../contextProviders/AppStateProvider";
import { useMsal } from "@azure/msal-react";
import { userActions, apiList, methodType } from "../../contextProviders/constant";
import authApi from "../../contextProviders/auth-api";
import getToken from "../../util/tokenGenerator";
import { handleTokenRequest } from "../../util/helper";
import { isChangePlanStored, getChangePlanData } from "./qbo_AccountingHelper";
import { SUBSCRIPTION_STATUS } from "./constants";

/**
 * Guard hook for QBO subpages (Pricing, Terms, OauthCallback).
 * Checks feature flag, then connection, then eligibility.
 * Redirects to QBO accounting page if any check fails or blocks access.
 * @param {Object} params - { lang, history }
 * @returns {Object} - { isReady: boolean, subscription: Object|null }
 */
function useAccountingGuard({ lang, history, skip = false }) {
  const { appState, updateAppStateLocal } = useAppStateProvider();
  const { instance, inProgress, accounts } = useMsal();
  const getTokenHandler = () => getToken(instance, inProgress, accounts);
  const [subscription, setSubscription] = useState(null);

  const qboAccountingPath = `/${lang}/qbo-accounting`;

  // Restore change-plan mode from sessionStorage (survives full-page reload)
  const storedChangePlan = isChangePlanStored();
  const shouldSkip = skip || storedChangePlan;
  const [isReady, setIsReady] = useState(shouldSkip);

  // Restore appState from sessionStorage if needed
  useEffect(() => {
    if (storedChangePlan && !appState?.changePlanMode) {
      const data = getChangePlanData();
      updateAppStateLocal({
        changePlanMode: true,
        changePlanNewFrequency: data?.newFrequency || null,
      });
    }
  }, [storedChangePlan]);

  useEffect(() => {
    // Skip guard entirely (e.g. change-plan mode)
    if (shouldSkip) {
      setIsReady(true);
      return;
    }

    // Wait for feature flag to resolve
    if (appState.isAccountingEnabled === null) return;

    // Feature flag disabled → redirect home (handled by useAccountingFeatureFlag)
    if (appState.isAccountingEnabled === false) return;

    // Feature flag passed → check connection + eligibility
    const checkAccess = async () => {
      try {
        const response = await authApi({
          url: apiList[userActions.getUIConnections],
          method: methodType.get,
          tokenhandler: async () => await handleTokenRequest(getTokenHandler),
          isSecured: true,
        });

        const qboConnection = response?.data?.connections?.find(
          (connection) => connection.partner_company_code === "qbo",
        );

        const isConnected = qboConnection?.token_valid ?? false;
        const { subscription: apiSubscription = null } = response?.data ?? {};
        const { status: subscriptionStatus } = apiSubscription ?? {};

        const setupStatuses = [SUBSCRIPTION_STATUS.PLAN_SELECTED, SUBSCRIPTION_STATUS.PENDING];
        const hasCompletedSetup = apiSubscription && !setupStatuses.includes(subscriptionStatus);

        // Connected user who already completed setup → redirect to main page
        if (isConnected && hasCompletedSetup) {
          history.push(qboAccountingPath);
          return;
        }

        // Check eligibility
        const eligibilityResponse = await authApi({
          body: {},
          method: methodType.get,
          url: apiList[userActions.qboConnectionEligibility],
          tokenhandler: async () => await handleTokenRequest(getTokenHandler),
          isSecured: true,
          headers: { "api-version": "v1" },
        });

        if (!eligibilityResponse?.data || eligibilityResponse?.status >= 400) {
          history.push(qboAccountingPath);
          return;
        }

        const eligibility = eligibilityResponse?.data?.eligibility ?? null;
        const eligibilitySubscription = eligibilityResponse?.data?.subscription ?? null;

        // Store subscription from eligibility in local state and appState
        setSubscription(eligibilitySubscription);
        updateAppStateLocal({ qboSubscription: eligibilitySubscription });

        const blockedStatuses = [
          "multiple_mids",
          "mid_already_connected",
          "no_mids_assigned",
        ];

        if (blockedStatuses.includes(eligibility)) {
          sessionStorage.setItem(
            "qboEligibilityRedirect",
            JSON.stringify({ eligibility }),
          );
          history.push(qboAccountingPath);
          return;
        }

        // All checks passed
        setIsReady(true);
      } catch {
        history.push(qboAccountingPath);
      }
    };

    checkAccess();
  }, [appState.isAccountingEnabled, shouldSkip]);

  return { isReady, subscription };
}

export default useAccountingGuard;
